import React, { useState, lazy } from 'react';
import { PhoneIcon, MailIcon, MapPinIcon, ClockIcon, SendIcon } from 'lucide-react';
export function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  }
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
  }
  return <section id="contact" className="w-full bg-brand-dark py-16 md:py-24 border-t border-brand-border" aria-labelledby="contact-heading" data-id="element-1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16" data-id="element-3">
          <h2 id="contact-heading" className="font-oswald font-bold text-[28px] sm:text-3xl md:text-4xl lg:text-5xl uppercase tracking-wide text-white mb-4" data-id="element-4">
            Get in Touch
          </h2>
          <p className="text-brand-muted text-base md:text-lg max-w-2xl mx-auto" data-id="element-5">
            Ready to buy, sell, or just have questions? Reach out — we'd love to
            hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-6">
          {/* Left — Contact Info */}
          <div className="space-y-8" data-id="element-7">
            <div className="space-y-6" data-id="element-8">
              <a href="tel:+16232998071" className="flex items-start gap-4 group" data-id="element-9">
                <div className="w-12 h-12 rounded-lg bg-brand-orange/10 flex items-center justify-center flex-shrink-0" data-id="element-10">
                  <PhoneIcon className="w-6 h-6 text-brand-orange" aria-hidden="true" data-id="element-11" />
                </div>
                <div data-id="element-12">
                  <p className="font-oswald font-semibold text-white uppercase text-sm mb-1" data-id="element-13">
                    Phone
                  </p>
                  <p className="text-brand-muted group-hover:text-brand-orange transition-colors" data-id="element-14">
                    (623) 299-8071
                  </p>
                </div>
              </a>

              <a href="mailto:info@payneequipment.com" className="flex items-start gap-4 group" data-id="element-15">
                <div className="w-12 h-12 rounded-lg bg-brand-orange/10 flex items-center justify-center flex-shrink-0" data-id="element-16">
                  <MailIcon className="w-6 h-6 text-brand-orange" aria-hidden="true" data-id="element-17" />
                </div>
                <div data-id="element-18">
                  <p className="font-oswald font-semibold text-white uppercase text-sm mb-1" data-id="element-19">
                    Email
                  </p>
                  <p className="text-brand-muted group-hover:text-brand-orange transition-colors" data-id="element-20">
                    info@payneequipment.com
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-4" data-id="element-21">
                <div className="w-12 h-12 rounded-lg bg-brand-orange/10 flex items-center justify-center flex-shrink-0" data-id="element-22">
                  <MapPinIcon className="w-6 h-6 text-brand-orange" aria-hidden="true" data-id="element-23" />
                </div>
                <div data-id="element-24">
                  <p className="font-oswald font-semibold text-white uppercase text-sm mb-1" data-id="element-25">
                    Location
                  </p>
                  <p className="text-brand-muted" data-id="element-26">Peoria, AZ 85382</p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-27">
                <div className="w-12 h-12 rounded-lg bg-brand-orange/10 flex items-center justify-center flex-shrink-0" data-id="element-28">
                  <ClockIcon className="w-6 h-6 text-brand-orange" aria-hidden="true" data-id="element-29" />
                </div>
                <div data-id="element-30">
                  <p className="font-oswald font-semibold text-white uppercase text-sm mb-1" data-id="element-31">
                    Hours
                  </p>
                  <p className="text-brand-muted" data-id="element-32">Mon–Fri: 7:00 AM – 5:00 PM</p>
                  <p className="text-brand-muted" data-id="element-33">Saturday: By Appointment</p>
                  <p className="text-brand-muted" data-id="element-34">Sunday: Closed</p>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="w-full aspect-[16/9] bg-brand-card rounded-lg border border-brand-border overflow-hidden" data-id="element-35">
              <iframe title="Payne Equipment Sales location — Peoria, AZ" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d53244.54590399498!2d-112.27037565!3d33.5805955!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x872b6890f0753c69%3A0x1e4b9e8c1e4b9e8c!2sPeoria%2C%20AZ%2085382!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus" className="w-full h-full border-0" allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-36" />
            </div>
          </div>

          {/* Right — Contact Form */}
          <div className="bg-brand-card rounded-lg border border-brand-border p-6 md:p-8" data-id="element-37">
            {submitted ? <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center" data-id="element-38">
                <div className="w-16 h-16 rounded-full bg-brand-orange/15 flex items-center justify-center mb-6" data-id="element-39">
                  <SendIcon className="w-8 h-8 text-brand-orange" aria-hidden="true" data-id="element-40" />
                </div>
                <h3 className="font-oswald font-bold text-2xl text-white uppercase mb-3" data-id="element-41">
                  Message Sent!
                </h3>
                <p className="text-brand-muted max-w-sm" data-id="element-42">
                  Thanks for reaching out. We'll get back to you within 24
                  hours.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-43">
                <div data-id="element-44">
                  <label htmlFor="name" className="block font-oswald text-sm uppercase tracking-wider text-white mb-2" data-id="element-45">
                    Full Name <span className="text-brand-orange" data-id="element-46">*</span>
                  </label>
                  <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} placeholder="John Smith" className="w-full px-4 py-3 bg-brand-dark border border-brand-border rounded-md text-base text-white placeholder:text-brand-muted/50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all min-h-[48px]" data-id="element-47" />
                </div>

                <div data-id="element-48">
                  <label htmlFor="email" className="block font-oswald text-sm uppercase tracking-wider text-white mb-2" data-id="element-49">
                    Email <span className="text-brand-orange" data-id="element-50">*</span>
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} placeholder="john@example.com" className="w-full px-4 py-3 bg-brand-dark border border-brand-border rounded-md text-base text-white placeholder:text-brand-muted/50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all min-h-[48px]" data-id="element-51" />
                </div>

                <div data-id="element-52">
                  <label htmlFor="phone" className="block font-oswald text-sm uppercase tracking-wider text-white mb-2" data-id="element-53">
                    Phone
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="(623) 555-1234" className="w-full px-4 py-3 bg-brand-dark border border-brand-border rounded-md text-base text-white placeholder:text-brand-muted/50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all min-h-[48px]" data-id="element-54" />
                </div>

                <div data-id="element-55">
                  <label htmlFor="message" className="block font-oswald text-sm uppercase tracking-wider text-white mb-2" data-id="element-56">
                    Message <span className="text-brand-orange" data-id="element-57">*</span>
                  </label>
                  <textarea id="message" name="message" required rows={5} value={formData.message} onChange={handleChange} placeholder="Tell us what equipment you're looking for, or ask us anything..." className="w-full px-4 py-3 bg-brand-dark border border-brand-border rounded-md text-base text-white placeholder:text-brand-muted/50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all resize-none" data-id="element-58" />
                </div>

                <button type="submit" className="w-full px-8 py-4 bg-brand-orange text-white font-oswald font-semibold text-base uppercase tracking-wider rounded-md hover:bg-brand-orange/90 transition-colors duration-200 min-h-[48px]" data-id="element-59">
                  Send Message
                </button>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}